/**
 * HOCP Operational Design System — v1 · Primitives
 * ────────────────────────────────────────────────────────────────────
 * The ONLY components operational modules should use for buttons, cards,
 * status, drawers, action bars, tiles and search. Direct use of raw shadcn
 * <Button>, <Card>, etc. inside operational screens is discouraged.
 *
 * Every primitive:
 *   - Accepts visible strings via `label` / `sublabel` props (never children
 *     for the primary label — enables i18n wrap later without touching call sites)
 *   - Meets touch-target minimums (48×48, primary CTAs 56 px)
 *   - Uses colour + text + icon on every status (never colour alone)
 *   - Carries a mandatory `data-testid` prop
 *
 * Primitives exported:
 *   1. OperationalButton   — big labelled button, primary/secondary/danger
 *   2. OperationalCard     — 20 px pad, 12 px radius, obvious hit-area
 *   3. StatusChip          — icon + label + tone (small / medium / large)
 *   4. PrimaryActionBar    — top-of-screen action row (max 3 actions)
 *   5. QuickActionTile     — big tap-target with icon + label + count
 *   6. TaskCard            — operational work item (title + status + meta)
 *   7. ConfirmSheet        — undo-preferred confirm; bottom sheet on mobile
 *   8. OperationalDrawer   — right-slide sheet with header + body + footer
 *   9. BottomActionBar     — sticky bottom bar (primary + secondary)
 *  10. LargeSearchBox      — 48 px+ search with clear + submit
 */
import React from "react";
import { X, Search as SearchIcon, CircleAlert } from "lucide-react";
import {
  Sheet, SheetContent, SheetTitle,
} from "@/components/ui/sheet";
import { getStatusToken, TOUCH, TEXT, t } from "./tokens";

// ═══════════════════════════════════════════════════════════════════
// 1. OperationalButton
// ═══════════════════════════════════════════════════════════════════
const BUTTON_TONES = {
  primary:   "bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white border-sky-600",
  secondary: "bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-800 border-slate-300",
  success:   "bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white border-emerald-600",
  warning:   "bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white border-orange-500",
  danger:    "bg-red-600 hover:bg-red-700 active:bg-red-800 text-white border-red-600",
};

export function OperationalButton({
  label, sublabel, icon: Icon, tone = "primary",
  size = "standard",  // primary | standard | secondary
  onClick, disabled, loading, fullWidth, testid, autoFocus,
}) {
  if (!label) console.warn("[ODS] OperationalButton requires a `label` prop");
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      autoFocus={autoFocus}
      data-testid={testid}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-xl border-2",
        "font-semibold transition-all",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "active:scale-[0.98]",
        TOUCH[size] || TOUCH.standard,
        BUTTON_TONES[tone] || BUTTON_TONES.primary,
        fullWidth ? "w-full" : "px-6",
      ].join(" ")}
    >
      {Icon && <Icon className="w-5 h-5 shrink-0" strokeWidth={2.4}/>}
      <span className="text-left leading-tight">
        <span className="block">{label}</span>
        {sublabel && (
          <span className="block text-[11px] font-normal opacity-80">{sublabel}</span>
        )}
      </span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 2. OperationalCard
// ═══════════════════════════════════════════════════════════════════
export function OperationalCard({
  children, onClick, testid, tone = "neutral", className = "",
  padded = true, interactive,
}) {
  const toneBorder = {
    neutral: "border-slate-200",
    info:    "border-sky-300",
    warning: "border-amber-400",
    danger:  "border-red-400",
  }[tone] || "border-slate-200";

  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={[
        "block w-full text-left rounded-xl border-2 bg-white",
        (padded ? "p-5" : ""),
        toneBorder,
        (onClick || interactive)
          ? "transition-all hover:border-sky-500 hover:shadow-md active:scale-[0.99] cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300"
          : "",
        className,
      ].filter(Boolean).join(" ")}
    >
      {children}
    </Component>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 3. StatusChip
// ═══════════════════════════════════════════════════════════════════
export function StatusChip({ status, label, size = "md", testid }) {
  const tok = getStatusToken(status);
  const Icon = tok.icon;
  const sizeClasses = {
    sm: "px-2 py-0.5 text-[11px] gap-1",
    md: "px-3 py-1 text-[13px] gap-1.5",
    lg: "px-4 py-2 text-[15px] gap-2",
  }[size] || "";
  const iconSize = size === "lg" ? "w-4 h-4" : size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  return (
    <span
      data-testid={testid || `status-chip-${tok.key}`}
      className={[
        "inline-flex items-center rounded-full border font-medium whitespace-nowrap",
        tok.tone.bg, tok.tone.border, tok.tone.text,
        sizeClasses,
      ].join(" ")}
    >
      <Icon className={iconSize} strokeWidth={2.4} />
      <span>{label || t(`status.${tok.key}`, tok.label)}</span>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 4. PrimaryActionBar — top-of-screen (max 3 actions per ADR-014)
// ═══════════════════════════════════════════════════════════════════
export function PrimaryActionBar({ title, subtitle, actions = [], testid }) {
  if (actions.length > 3) {
    console.warn(`[ODS] PrimaryActionBar received ${actions.length} actions; max 3.`);
  }
  return (
    <div data-testid={testid} className="flex flex-wrap gap-3 items-start justify-between">
      <div>
        <h1 className={TEXT.hero}>{title}</h1>
        {subtitle && <p className="text-[14px] text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className="flex gap-2">
        {actions.slice(0, 3).map((a, i) => (
          <OperationalButton key={a.testid || i} {...a} />
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 5. QuickActionTile — big tap-target with count
// ═══════════════════════════════════════════════════════════════════
export function QuickActionTile({
  label, count, icon: Icon, status = "ready",
  onClick, testid,
}) {
  const tok = getStatusToken(status);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={[
        "flex flex-col items-start justify-between text-left",
        "min-h-[112px] w-full rounded-2xl border-2 p-5",
        "transition-all active:scale-[0.98]",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300",
        tok.tone.bg, tok.tone.border, tok.tone.hoverBorder,
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`w-5 h-5 ${tok.tone.text}`} strokeWidth={2.2}/>}
        <span className={`${TEXT.metaSm} font-semibold ${tok.tone.text}`}>
          {label}
        </span>
      </div>
      <span className={`w-full block text-[19px] sm:text-[20px] xl:text-[22px] 2xl:text-[26px] font-bold ${tok.tone.text} leading-none tabular-nums whitespace-nowrap`}>
        {count ?? 0}
      </span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 6. TaskCard — canonical operational work item
// ═══════════════════════════════════════════════════════════════════
export function TaskCard({
  title, sublabel, status, statusLabel,
  meta = [],           // array of {icon?, label}
  primaryAction,       // {label, icon, onClick, tone}
  onOpen, testid,
}) {
  return (
    <OperationalCard onClick={onOpen} testid={testid} padded={false}>
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className={TEXT.card + " truncate"}>{title}</div>
            {sublabel && (
              <div className="text-[13px] text-slate-500 mt-0.5 truncate">
                {sublabel}
              </div>
            )}
          </div>
          {status && (
            <StatusChip status={status} label={statusLabel} size="md"
                          testid={testid ? `${testid}-status` : undefined}/>
          )}
        </div>
        {meta.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-slate-600">
            {meta.map((m, i) => (
              <span key={i} className="inline-flex items-center gap-1.5">
                {m.icon && React.createElement(m.icon,
                  { className: "w-3.5 h-3.5 text-slate-400", strokeWidth: 2 })}
                {m.label}
              </span>
            ))}
          </div>
        )}
        {primaryAction && (
          <OperationalButton {...primaryAction}
             size="standard" fullWidth
             testid={testid ? `${testid}-primary` : undefined}/>
        )}
      </div>
    </OperationalCard>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 7. ConfirmSheet — bottom sheet on mobile, prefer undo-toast where possible
// ═══════════════════════════════════════════════════════════════════
export function ConfirmSheet({
  open, onOpenChange,
  title, body,
  confirmLabel, confirmTone = "danger",
  cancelLabel = "Cancel",
  onConfirm, testid,
  loading,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom"
        className="p-0 rounded-t-2xl sm:max-w-[520px] sm:mx-auto"
        data-testid={testid || "confirm-sheet"}>
        <SheetTitle className="sr-only">{title}</SheetTitle>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <CircleAlert className={`w-6 h-6 shrink-0 ${
              confirmTone === "danger" ? "text-red-600" : "text-sky-600"}`}/>
            <div>
              <div className={TEXT.card}>{title}</div>
              {body && (
                <div className="text-[14px] text-slate-600 mt-1">{body}</div>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <OperationalButton label={cancelLabel} tone="secondary"
              size="standard" fullWidth onClick={() => onOpenChange(false)}
              testid={testid ? `${testid}-cancel` : "confirm-cancel"}/>
            <OperationalButton label={confirmLabel} tone={confirmTone}
              size="standard" fullWidth onClick={onConfirm} loading={loading}
              testid={testid ? `${testid}-confirm` : "confirm-yes"}/>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 8. OperationalDrawer — right slide-in with header + body + footer
// ═══════════════════════════════════════════════════════════════════
export function OperationalDrawer({
  open, onOpenChange, title, subtitle, statusChip,
  children, footer,
  size = "md",  // sm | md | lg
  testid,
}) {
  const width = { sm: "sm:max-w-[420px]", md: "sm:max-w-[640px]", lg: "sm:max-w-[820px]" }[size] || "";
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right"
        hideCloseButton
        className={`w-full ${width} p-0 flex flex-col`}
        data-testid={testid || "operational-drawer"}>
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {/* Header */}
        <div className="p-5 border-b border-slate-200 bg-slate-50 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={TEXT.card + " truncate"}>{title}</span>
              {statusChip}
            </div>
            {subtitle && (
              <div className="text-[13px] text-slate-500 truncate">{subtitle}</div>
            )}
          </div>
          <OperationalButton
            label="" icon={X} tone="secondary" size="secondary"
            onClick={() => onOpenChange(false)}
            testid={testid ? `${testid}-close` : "drawer-close"}/>
        </div>
        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">{children}</div>
        {/* Footer — optional */}
        {footer && (
          <div className="p-4 border-t border-slate-200 bg-white">{footer}</div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 9. BottomActionBar — sticky bottom bar (primary + secondary)
// ═══════════════════════════════════════════════════════════════════
export function BottomActionBar({
  primary,             // {label, icon, onClick, tone, loading}
  secondaryActions = [],  // [{label, icon, onClick, tone}]
  helper,              // optional text between the two groups
  testid,
}) {
  return (
    <div data-testid={testid || "bottom-action-bar"}
      className="sticky bottom-0 z-10 border-t border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-2">
      <div className="flex-1 flex items-center gap-2 min-w-0">
        {secondaryActions.map((a, i) => (
          <OperationalButton key={a.testid || i} {...a} size="secondary" tone={a.tone || "secondary"}/>
        ))}
      </div>
      {helper && (
        <div className="text-[12px] text-slate-500 mx-2 hidden md:block">{helper}</div>
      )}
      {primary && (
        <OperationalButton {...primary} size="primary"/>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 10. LargeSearchBox — 48-px height search with clear + submit
// ═══════════════════════════════════════════════════════════════════
export function LargeSearchBox({
  value, onChange, onSubmit, onClear,
  placeholder, testid, autoFocus,
}) {
  return (
    <div data-testid={testid || "large-search"}
      className="flex items-center gap-2 h-12 rounded-xl border-2 border-slate-300 bg-white px-4 focus-within:border-sky-500 transition-colors">
      <SearchIcon className="w-5 h-5 text-slate-400 shrink-0" strokeWidth={2.2}/>
      <input
        type="search"
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSubmit?.(value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testid ? `${testid}-input` : "large-search-input"}
        className="flex-1 outline-none bg-transparent text-[16px] placeholder:text-slate-400"
      />
      {value && (
        <button type="button"
          onClick={() => { onChange?.(""); onClear?.(); }}
          data-testid={testid ? `${testid}-clear` : "large-search-clear"}
          className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
          <X className="w-4 h-4 text-slate-500"/>
        </button>
      )}
    </div>
  );
}

// ── Barrel re-exports ───────────────────────────────────────────────
export * from "./tokens";

// ── FO-1.2 · Operational Design System Extensions ──────────────────
// These primitives are consumed platform-wide by every operational module.
// Front Office is the first consumer (starting FO-1.3); Housekeeping,
// Engineering, Reservations, Dashboard follow during their retrofits.
export { GuestWorkspace } from "./GuestWorkspace";
export { RoomOperationalCard } from "./RoomOperationalCard";
export { FloorRoomPicker } from "./FloorRoomPicker";
export { GuestSearchPanel } from "./GuestSearchPanel";
export { TodaysWorkPanel } from "./TodaysWorkPanel";
export { QuickActionGrid } from "./QuickActionGrid";
